import {useEffect, useState} from 'react'
import {serverLocalName} from 'GLOBAL/envVariables'

/**
 * 수용가 행을 클릭했을 때 어느 모달을 열지에 대한 사업소별 설정.
 *
 * 목록에 있는 사업소는 **사진 모달**, 없으면 **수용가 상세 모달**이 열린다.
 * 사진첩 배지도 같은 판정을 따른다.
 *
 * 사업소가 늘거나 요청이 바뀔 때마다 `globalController.photoHandler` 의 switch 를
 * 고쳐 재배포해야 했다(장성 D7 추가처럼). 설치 판정 기준(signalConfig)·통신주기
 * (serverCycleConfig)와 같은 방식으로 서버의 JSON 파일만 교체하면 되도록 분리한다.
 *
 * 파일이 없거나 형식이 어긋나면 기본값(기존 하드코딩 값)으로 계속 동작한다 —
 * 모달이 엉뚱하게 바뀌는 것보다 이전 동작을 유지하는 쪽이 안전하다.
 */

export interface PhotoOffice {
  /**
   * 사진 모달을 여는 사업소 코드. `*` 면 이 서버의 모든 사업소.
   *
   * 코드 체계상 하위 사업소는 상위 코드로 시작하므로(2자리 본부 → 4자리 사업소 →
   * 6자리 상수/지하수), 본부 코드를 넣으면 그 아래 전부가 포함된다.
   */
  officeId: string
  /** 화면에 표시할 이름. 판정에는 쓰지 않는 메모다. */
  label: string
}

export interface PhotoConfig {
  offices: PhotoOffice[]
}

/**
 * 판정은 **사업소 단위**다. 통신주기(serverCycleConfig)처럼 화면별로 나누는 것도
 * 검토했지만(수용가 정보에서만 사진 모달을 열고 현황에서는 상세를 여는 식),
 * 실제 요청이 없어 두기로 했다.
 *
 * 나중에 필요해지면: `PhotoOffice` 에 `pages` 를 두고 `isPhotoModalOffice(officeId, page)`
 * 로 바꾼 뒤, `declearColumns` 의 호출부마다 화면 키를 리터럴로 넘기면 된다.
 * 단 `customerStatusBoseongColumns` 는 현황·검침 두 화면이 함께 쓰므로 리터럴이 아니라
 * 인자로 받아야 한다. 편집 UI 는 칩 목록이 아니라 통신주기와 같은 탭 구조가 된다.
 */

/** 이 서버의 모든 사업소를 뜻하는 officeId */
export const ALL_OFFICES = '*'

/** 설정 파일 경로. docker-compose 볼륨 마운트 대상이라 임의로 바꾸지 않는다. */
export const PHOTO_CONFIG_PATH = '/config/photo-config.json'

/**
 * 기본값. 설정 파일이 없을 때 쓰이며, 기존 `photoHandler` switch 그대로다.
 *
 * prod 와 dev 의 목록이 원래 달랐다(dev 에는 60·66 이 없다). 설정 파일은 서버마다
 * 볼륨으로 따로 붙으므로 이 차이는 파일이 나뉘는 것으로 자연스럽게 유지된다.
 */
export const defaultPhotoConfig = (serverName: string): PhotoConfig => {
  switch (serverName) {
    case 'prod':
      return {
        offices: [
          {officeId: '84', label: '진주'},
          {officeId: '13', label: '부산'},
          {officeId: 'A1', label: '창녕'},
          {officeId: '60', label: '광주'},
          /* 주석에 지역명이 없던 코드다. /dev 에서 사업소를 다시 고르면 이름이 채워진다. */
          {officeId: '66', label: ''},
          {officeId: 'D7', label: '장성'},
        ],
      }
    case 'dev':
      return {
        offices: [
          {officeId: '84', label: '진주'},
          {officeId: '13', label: '부산'},
          {officeId: 'A1', label: '창녕'},
          {officeId: 'D7', label: '장성'},
        ],
      }
    default:
      return {offices: []}
  }
}

/** 현재 적용 중인 설정. 판정이 컬럼 정의(비 컴포넌트)에서 동기로 읽히므로 모듈 레벨에 둔다. */
let currentConfig: PhotoConfig = defaultPhotoConfig(serverLocalName)

export interface PhotoConfigStatus {
  /** default = 기본값으로 동작 중, remote = 설정 파일 적용됨 */
  source: 'default' | 'remote'
  loadedAt: Date | null
  /** 마지막 실패 사유. 서버에 접속하지 않고 원인을 확인해야 하므로 남겨둔다. */
  error: string | null
}

let currentStatus: PhotoConfigStatus = {source: 'default', loadedAt: null, error: null}

export const getPhotoConfig = (): PhotoConfig => currentConfig
export const getPhotoConfigStatus = (): PhotoConfigStatus => currentStatus

/**
 * 이 사업소가 행 클릭 시 사진 모달을 여는지.
 *
 * 컬럼 정의 모듈에서 렌더 중 동기로 불리므로 훅이 아니라 함수다.
 * 설정이 도착하기 전에는 기본값으로 답한다.
 *
 * **사업소를 `전체`로 조회하면(officeId 가 `'all'`) 어떤 코드와도 맞지 않아 false 다.**
 * 목록에 진주 수용가가 섞여 있어도 수용가 상세 모달이 열린다. 기존 `photoHandler`
 * (`'all'.slice(0,2)` = `'al'`) 도 같은 결과였고, 그대로 두기로 한 동작이다.
 * 행마다 다르게 열려면 판정 인자를 `params.row.officeId` 로 바꾸면 된다
 * (행 데이터에 officeId 가 들어 있다) — 전체 목록에서 행마다 다른 모달이 열리는 쪽을
 * 택하지 않았을 뿐이다.
 */
export const isPhotoModalOffice = (officeId: any): boolean => {
  const id = String(officeId ?? '')
  return currentConfig.offices.some(
    (office) =>
      office.officeId === ALL_OFFICES || (office.officeId !== '' && id.startsWith(office.officeId))
  )
}

/**
 * 외부 JSON 을 검증해 PhotoConfig 로 변환한다.
 * 한 군데라도 어긋나면 통째로 거부한다 — 사업소 절반만 반영되면 원인을 찾기 더 어렵다.
 *
 * @returns 유효하면 설정, 아니면 사유 문자열
 */
export const parsePhotoConfig = (raw: any): PhotoConfig | string => {
  if (!raw || typeof raw !== 'object') return '설정 형식이 올바르지 않습니다.'
  if (!Array.isArray(raw.offices)) return 'offices 는 배열이어야 합니다.'

  const offices: PhotoOffice[] = []
  const seen = new Set<string>()

  for (let index = 0; index < raw.offices.length; index++) {
    const row = raw.offices[index]
    const rowLabel = `${index + 1}번째 사업소의`
    if (!row || typeof row !== 'object') return `${rowLabel} 형식이 올바르지 않습니다.`

    const {officeId, label} = row
    if (typeof officeId !== 'string' || officeId.trim() === '') {
      return `${rowLabel} 사업소 ID 가 비어 있습니다.`
    }
    const id = officeId.trim()
    /* 같은 사업소가 두 번 나와도 판정은 같지만, 화면에 중복 표시돼 혼란스럽다 */
    if (seen.has(id)) return `사업소 ID ${id} 가 중복됩니다.`
    seen.add(id)

    if (typeof label !== 'undefined' && typeof label !== 'string') {
      return `${rowLabel} 이름은 문자열이어야 합니다.`
    }

    offices.push({officeId: id, label: label ?? ''})
  }

  return {offices}
}

/** 편집 UI 에서 저장할 때 쓰는 직렬화. */
export const stringifyPhotoConfig = (config: PhotoConfig): string =>
  JSON.stringify(
    {
      offices: config.offices.map((office) => ({
        officeId: office.officeId,
        label: office.label,
      })),
    },
    null,
    2
  )

/**
 * 여러 화면이 동시에 설정을 필요로 하므로 진행 중인 요청 하나를 함께 기다린다.
 */
let inFlight: Promise<PhotoConfig> | null = null

/**
 * 설정 파일을 읽어 적용한다.
 * 어떤 실패에서도 예외를 던지지 않고, 직전 설정(없으면 기본값)을 그대로 유지한다.
 */
export const loadPhotoConfig = (timeoutMs: number = 3000): Promise<PhotoConfig> => {
  if (inFlight) return inFlight

  inFlight = (async () => {
    /* 통신이 느린 환경에서 응답을 기다리며 화면이 멈추지 않도록 제한을 둔다 */
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const res = await fetch(`${PHOTO_CONFIG_PATH}?ts=${Date.now()}`, {
        cache: 'no-store',
        signal: controller.signal,
      })
      const contentType = res.headers.get('content-type') ?? ''

      /* 파일이 없으면 nginx SPA fallback 이 index.html 을 200 으로 돌려주므로
         상태 코드만으로는 판별할 수 없다. */
      if (!res.ok || !contentType.includes('json')) {
        currentStatus = {...currentStatus, error: '설정 파일을 찾을 수 없습니다.'}
        return currentConfig
      }

      const parsed = parsePhotoConfig(await res.json())
      if (typeof parsed === 'string') {
        currentStatus = {...currentStatus, error: parsed}
        return currentConfig
      }

      currentConfig = parsed
      currentStatus = {source: 'remote', loadedAt: new Date(), error: null}
      notify()
    } catch {
      currentStatus = {...currentStatus, error: '설정 파일을 불러오지 못했습니다.'}
    } finally {
      clearTimeout(timer)
      inFlight = null
    }
    return currentConfig
  })()

  return inFlight
}

/**
 * 설정이 도착했을 때 다시 그려야 하는 화면들.
 *
 * 판정을 읽는 곳 대부분이 컬럼 정의라 훅을 쓸 수 없어, 화면 쪽에서 구독해
 * 리렌더를 유도한다. 컬럼은 렌더마다 다시 만들어지므로 화면 하나만 다시 그리면
 * 그 화면의 컬럼 판정도 함께 갱신된다.
 */
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((listener) => listener())

export const subscribePhotoConfig = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * 이 사업소가 사진 모달을 여는지. 설정이 도착하면 화면을 다시 그린다.
 *
 * 컬럼 정의에서 부르는 `isPhotoModalOffice` 는 스스로 리렌더를 일으킬 수 없으므로,
 * 컬럼을 만드는 화면이 이 훅을 하나 걸어두면 그 화면의 컬럼까지 함께 갱신된다.
 */
export const usePhotoModalOffice = (officeId: any): boolean => {
  const [enabled, setEnabled] = useState(() => isPhotoModalOffice(officeId))

  useEffect(() => {
    const sync = () => setEnabled(isPhotoModalOffice(officeId))
    const unsubscribe = subscribePhotoConfig(sync)
    /* 화면에 들어올 때마다 확인하므로 설정을 바꾼 뒤 새로고침 없이 반영된다 */
    loadPhotoConfig().then(sync)
    return unsubscribe
  }, [officeId])

  return enabled
}
